// Designer live-embed preview — pure support layer.
//
// The form designer (/settings/form-design) renders the REAL embed inline,
// fed the merchant's UNSAVED draft config, instead of a hand-built React
// mock. That kills preview drift by construction: there is only one form
// renderer, so the preview cannot disagree with the storefront.
//
// This module holds the DOM-free half of that feature (option normalization
// + the demo line items), so it can be unit-tested under vitest's node
// environment. The mount itself lives in index.ts (`renderPreview`), which
// needs the QuoteBuilder class + a real document.

/** Line item shape a caller may hand us via `opts.sampleItems`. Mirrors the
 *  embed's CartItem loosely — every field but `variantId` is optional and
 *  gets a sensible demo default. */
export interface PreviewSampleItem {
  variantId: string | number;
  productId?: string | number;
  title?: string;
  variantTitle?: string;
  sku?: string;
  qty?: number;
  price?: string | number;
  vendor?: string;
  imageUrl?: string | null;
}

/** The embed's CartItem subset the preview seeds. Structurally assignable to
 *  index.ts's CartItem (extra optional inventory fields stay unset — the demo
 *  rows are never sold-out). */
export interface PreviewCartItem {
  variantId: string;
  productId: string;
  title: string;
  variantTitle: string;
  sku: string;
  qty: number;
  price: string;
  vendor: string;
  imageUrl: string | null;
}

export interface RenderPreviewOptions {
  /** Which layout the preview renders. "multistep"/"full"/"wizard" all map to
   *  the paged wizard; "builder" maps to the single-page builder layout. This
   *  is what the designer's Multi-step / Builder toggle drives. */
  mode?: string;
  /** Override the demo line items. Pass `[]` for an empty Build step (the
   *  merchant sees the real empty state); omit for SAMPLE_PREVIEW_ITEMS. */
  sampleItems?: PreviewSampleItem[] | null;
  /** Which wizard step to open on. Omit to preserve whatever step the
   *  previous render of this container was showing (so a live re-render
   *  while the merchant edits doesn't yank them back to step 1). */
  activeStep?: number;
  /** "quick"/"quick_expandable" renders the single-screen quick form;
   *  anything else (default) renders the full form. */
  formMode?: string;
  /** Render the post-submit confirmation screen instead of a wizard step —
   *  the designer's "✓ Confirmation" tab. Seeds `state.submitted = -1` (the
   *  embed's own "submitted, no sequence number" sentinel) so the real
   *  confirmation branch paints with the merchant's copy and no `#QR-N`.
   *  Mutually exclusive with `activeStep`; when both are set, confirmation
   *  wins. */
  showConfirmation?: boolean;
}

export interface NormalizedPreviewOptions {
  layoutMode: "wizard" | "builder";
  formMode: "full" | "quick_expandable";
  items: PreviewCartItem[];
  /** null = caller didn't pin a step; the mount carries the previous one
   *  over (or starts at 0 on a first render). */
  activeStep: number | null;
  showConfirmation: boolean;
}

/** Demo line items so the Build step shows real "Your items" cards and the
 *  pinned subtotal bar. An empty preview was exactly the old mock's gap: the
 *  merchant could never see the subtotal bar, the subtotal_note, or the item
 *  card treatment they were configuring. Priced so the subtotal is obviously
 *  sample data, not a real quote. */
export const SAMPLE_PREVIEW_ITEMS: readonly PreviewCartItem[] = Object.freeze([
  Object.freeze({
    variantId: "qs-preview-1",
    productId: "qs-preview-p1",
    title: "Sample product — power rack",
    variantTitle: "Black · 90 in",
    sku: "SAMPLE-001",
    qty: 2,
    price: "899.00",
    vendor: "Sample vendor",
    imageUrl: null,
  }),
  Object.freeze({
    variantId: "qs-preview-2",
    productId: "qs-preview-p2",
    title: "Sample product — bumper plate set",
    variantTitle: "260 lb",
    sku: "SAMPLE-002",
    qty: 1,
    price: "649.50",
    vendor: "Sample vendor",
    imageUrl: null,
  }),
]) as readonly PreviewCartItem[];

/** Coerce a caller-supplied sample item into the embed's CartItem shape.
 *  Prices normalize to the dollar STRING the renderer expects ("899.00"),
 *  accepting numbers so a caller can pass 899 or 899.5. */
export function toPreviewCartItem(raw: PreviewSampleItem): PreviewCartItem | null {
  const variantId = raw && raw.variantId != null ? String(raw.variantId).trim() : "";
  if (!variantId) return null;
  const price = typeof raw.price === "number"
    ? raw.price.toFixed(2)
    : (typeof raw.price === "string" && raw.price.trim()) ? raw.price.trim() : "0";
  const qty = Math.max(1, Math.floor(Number(raw.qty)) || 1);
  return {
    variantId,
    productId: raw.productId != null ? String(raw.productId) : "",
    title: raw.title || "Sample product",
    variantTitle: raw.variantTitle || "",
    sku: raw.sku || "",
    qty,
    price,
    vendor: raw.vendor || "",
    imageUrl: typeof raw.imageUrl === "string" && raw.imageUrl ? raw.imageUrl : null,
  };
}

/** Map + dedupe a caller's sample items. Invalid rows (no variantId) drop. */
export function toPreviewCartItems(raw: PreviewSampleItem[]): PreviewCartItem[] {
  const out: PreviewCartItem[] = [];
  for (const r of raw) {
    const item = toPreviewCartItem(r);
    if (!item) continue;
    if (out.some((c) => c.variantId === item.variantId)) continue;
    out.push(item);
  }
  return out;
}

/** Resolve renderPreview's loose option bag into the exact values the mount
 *  needs. Unknown/absent values fall back to the wizard + full form + the
 *  built-in demo items, which is what the designer's default toggle state
 *  asks for. */
export function normalizePreviewOptions(opts: RenderPreviewOptions = {}): NormalizedPreviewOptions {
  const mode = String(opts.mode || "").trim().toLowerCase();
  const rawFormMode = String(opts.formMode || "").trim().toLowerCase();
  const step = Number(opts.activeStep);
  return {
    layoutMode: mode === "builder" ? "builder" : "wizard",
    formMode: (rawFormMode === "quick" || rawFormMode === "quick_expandable")
      ? "quick_expandable"
      : "full",
    // `null` (explicitly cleared) and `undefined` both mean "use the demo
    // set"; an empty ARRAY is a deliberate "show me the empty state".
    items: Array.isArray(opts.sampleItems)
      ? toPreviewCartItems(opts.sampleItems)
      : SAMPLE_PREVIEW_ITEMS.map((i) => ({ ...i })),
    activeStep: Number.isFinite(step) && step >= 0 ? Math.floor(step) : null,
    showConfirmation: opts.showConfirmation === true,
  };
}
